import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { CalendarDays, CheckCircle2, AlertCircle, ExternalLink, Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECRET_KEY = "computheer_admin_secret";

const AdminConnect = () => {
  const [secret, setSecret] = useState(() => localStorage.getItem(SECRET_KEY) || "");
  const [secretInput, setSecretInput] = useState("");
  const [authError, setAuthError] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const justConnected = params.get("calendar_connected");

  const loadStatus = async (s = secret) => {
    if (!s) return;
    setLoading(true);
    setAuthError(false);
    try {
      const res = await axios.get(`${API}/oauth/calendar/status`, { params: { secret: s } });
      setStatus(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setAuthError(true);
        localStorage.removeItem(SECRET_KEY);
        setSecret("");
      } else {
        setStatus({ connected: false });
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (secret) loadStatus(secret);
  }, [secret]);

  const handleSecretSubmit = (e) => {
    e.preventDefault();
    if (!secretInput.trim()) return;
    localStorage.setItem(SECRET_KEY, secretInput.trim());
    setSecret(secretInput.trim());
    setSecretInput("");
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/oauth/calendar/login`, { params: { secret } });
      window.location.href = res.data.authorization_url;
    } catch {
      setConnecting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(SECRET_KEY);
    setSecret("");
    setStatus(null);
  };

  // === LOGIN SCREEN ===
  if (!secret) {
    return (
      <section className="min-h-[60vh] py-20 bg-neutral-50">
        <div className="max-w-md mx-auto px-5 md:px-10">
          <div className="bg-white border border-neutral-200 p-8">
            <div className="w-12 h-12 bg-neutral-900 text-white flex items-center justify-center mb-5">
              <Lock size={20} />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">Beheerderstoegang</h1>
            <p className="text-neutral-600 text-sm leading-relaxed mb-6">
              Deze pagina is alleen voor beheerders. Voer de admin-sleutel in om toegang te krijgen.
            </p>
            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-sm text-red-900">
                Onjuiste sleutel. Probeer opnieuw.
              </div>
            )}
            <form onSubmit={handleSecretSubmit} className="space-y-4">
              <input
                type="password"
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                autoFocus
                placeholder="Admin-sleutel"
                className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 text-sm"
              />
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-black text-white px-5 py-3 text-sm font-medium transition-colors"
              >
                Toegang krijgen
              </button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  // === CONNECT SCREEN ===
  return (
    <section className="min-h-[60vh] py-20 bg-neutral-50">
      <div className="max-w-2xl mx-auto px-5 md:px-10">
        <div className="bg-white border border-neutral-200 p-8 md:p-10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <CalendarDays size={22} className="text-neutral-900" />
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">Admin</p>
            </div>
            <button
              onClick={logout}
              className="text-xs text-neutral-500 hover:text-neutral-900 underline"
            >
              Uitloggen
            </button>
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-3">Google Calendar koppeling</h1>
          <p className="text-neutral-600 leading-relaxed mb-8">
            Koppel de Google Calendar van <strong>info@computheer.nl</strong> zodat bezette slots
            automatisch worden gefilterd en nieuwe aanvragen als voorlopige events worden
            toegevoegd. Bevestigingsmails worden via SMTP verstuurd vanaf <strong>info@computheer.nl</strong>.
          </p>

          {justConnected === "success" && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-900">
                <strong>Gelukt!</strong> Google Calendar is gekoppeld. Afspraken worden nu
                gesynchroniseerd.
              </p>
            </div>
          )}
          {justConnected === "error" && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start gap-3">
              <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-900">
                Er ging iets mis bij het koppelen: {params.get("reason") || "onbekende fout"}.
              </p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-neutral-500 text-sm">
              <Loader2 size={14} className="animate-spin" /> Status laden...
            </div>
          ) : status?.connected ? (
            <div className="p-5 bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 size={22} className="text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-900">Calendar gekoppeld</p>
                  <p className="text-sm text-emerald-700">{status.email}</p>
                </div>
              </div>
              <p className="text-sm text-emerald-900 mb-4">
                Agenda wordt gebruikt om bezette slots te filteren. Nieuwe afspraken worden
                automatisch als <em>tentatieve</em> events toegevoegd.
              </p>
              <button
                onClick={connect}
                disabled={connecting}
                className="inline-flex items-center gap-2 bg-white border border-emerald-300 hover:border-emerald-500 text-emerald-900 px-4 py-2 text-sm font-medium"
              >
                {connecting && <Loader2 size={14} className="animate-spin" />}
                Opnieuw koppelen
              </button>
            </div>
          ) : (
            <div className="p-5 bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle size={22} className="text-amber-600" />
                <p className="font-semibold text-amber-900">Nog niet gekoppeld</p>
              </div>
              <p className="text-sm text-amber-900 mb-4">
                Klik op onderstaande knop en geef toestemming om je Google Calendar te beheren.
              </p>
              <button
                onClick={connect}
                disabled={connecting}
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-black text-white px-5 py-3 text-sm font-medium transition-colors"
              >
                {connecting ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} />}
                {connecting ? "Doorverwijzen..." : "Koppel met Google Calendar"}
                <ExternalLink size={13} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminConnect;
